<template>
  <div class="matpreview p-3">
    <!-- If no image is currently selected or hovered, display a placeholder circle -->
    <div
      v-if="!currentImage"
      class="material-image material-circle-placeholder"
    />
    <!-- Otherwise, show the current image in a circle -->
    <img v-if="currentImage" :src="currentImage" class="material-image" >
    <div class="matpreview-container">
      <input
        type="text"
        v-model="filterText"
        :placeholder="placeholderText"
        class="form-control dropdown-toggle custom-select form-text-dark material-select darker"
        @focus="openDropdown"
        @blur="closeDropdown"
        autocomplete="off"
        ref="searchInput"
      >
      <div 
        class="form-error mt-2" 
        v-if="isMaterialHidden"
      >
        {{ $t("isMaterialHidden") }}
      </div>
      <div 
        class="form-error mt-2" 
        v-if="isMaterialInactive"
      >
        {{ $t("isMaterialInactive") }}
      </div>
      <div
        v-if="isDropdownOpen"
        class="dropdown-menu matpreview-dropdown-menu show form-text-dark"
      >
        <div
          v-for="(matItem, matIndex) in filteredItems"
          :key="matItem.id || matIndex"
          class="dropdown-item matpreview-dropdown-item"
          @mousedown.prevent="selectItem(matItem)"
          @mouseover="handleHover(matItem)"
        >
          {{ matItem.displayName ? matItem.displayName : matItem.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MatPreview",
  props: {
    item: { type: Object, required: true },
    allMaterials: { type: Array, required: true },
  },
  data() {
    return {
      filterText: "",
      isDropdownOpen: false,
      hoveredItem: null,
      selectedItem: null,
      isMaterialHidden: false,
      isMaterialInactive: false,
    };
  },
  computed: {
    filteredItems() {
      return this.allMaterials.filter((item) =>
        (item.displayName || item.name).toLowerCase().includes(this.filterText.toLowerCase())
      ).sort((a, b) => {
        const nameA = (a.displayName || a.name).toLowerCase();
        const nameB = (b.displayName || b.name).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    },
    currentImage() {
      if (this.hoveredItem) {
        return this.hoveredItem.image || "";
      }
      if (this.selectedItem) {
        return this.selectedItem.image || "";
      }
      return "";
    },
    placeholderText() {
      if (!this.isDropdownOpen && this.selectedItem) {
        return (
          this.selectedItem.displayName || this.selectedItem.name
        );
      }
      return "Search";
    },
  },
  watch: {
    allMaterials() {
      this.setInitialImage();
    },
    item: {
      handler() {
        this.fetchMaterialVisibility();
        this.setInitialImage();
      },
      immediate: true,
    },
  },
  methods: {
    /**
      * Focus on fetching the material's visibility state. (Hidden or Inactive)
      */
    async fetchMaterialVisibility() {
      const material = await this.$store.dispatch("clientLoadAsset", {
        id: this.item.material,
      });
      this.isMaterialHidden = material.state === "h";
      this.isMaterialInactive = material.state === "i";

      if (this.isMaterialHidden || this.isMaterialInactive) {
        const imageString = await this.$store.dispatch("loadThumbnail", { material });
        const selectedItem = {
          id: material.id,
          displayName: material.displayName,
          name: material.name,
          organizationId: material.organizationId,
          type: material.type,
          state: material.state,
          image: imageString,
        };
        this.selectedItem = selectedItem;
      }
    },
    /**
     * setInitialImage looks up the item in `allMaterials` that corresponds to the material ID in `this.item.material`.
     * If found, set `selectedItem`.
     * Else, clear `selectedItem`.
     */
    setInitialImage() {
      if (this.item && this.item.material) {
        const matItem = this.allMaterials.find(
          (item) => item.id === this.item.material
        );
        this.selectedItem = matItem || null;
      } else {
        this.selectedItem = null;
      }
    },
    openDropdown() {
      this.filterText = "";
      this.isDropdownOpen = true; 
    },
    closeDropdown() {
      this.filterText = "";
      this.hoveredItem = null;
      this.isDropdownOpen = false;
    },
    /**
     * HandleHover will automatically be called whenever the user hovers on any item in the dropdown.
     * @param {Object} matItem - The hovered item
     */
    handleHover(matItem) {
      this.hoveredItem = matItem;
    },
    /**
     * When an item has been clicked, the item will be selected and the dropdown will be closed.
     * @param matItem
     */
     selectItem(matItem) {
      this.item.material = matItem.id;
      this.filterText = matItem.displayName || matItem.name;
      this.selectedItem = matItem;
      this.isMaterialHidden = false;
      this.isMaterialInactive = false;
      this.closeDropdown();
      this.$refs.searchInput.blur();
      this.$emit('change')
    },
  },
};
</script>

<style lang="scss" scoped>
.matpreview {
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  position: relative;
  border-radius: 5px;
  background-color: #28272d;
  box-sizing: border-box;
}

.matpreview-container {
  position: relative;
  width: 100%;
  margin-left: 0.5rem;
}

.material-circle-placeholder {
  /* add dashed border */
  border: 2px dashed white;
}

.material-image {
  aspect-ratio: 1 / 1;
  width: 7rem;
  height: 7rem;
  border-radius: 50%;

  // object-fit: cover;
  box-sizing: border-box;
}

.material-dropdown {
  background-color: blue;
}

.matpreview-dropdown-menu {
  position: absolute;
  top: 23px;
  left: 0;
  z-index: 1000;
  display: block;
  color: #fff;
  width: 100%;
  background-color: #252525;
  border-color: #252525;
  max-height: 300px;
  min-height: 25px;
  overflow-y: auto;
  overflow-x: hidden; 
  box-sizing: border-box;
  border-radius: 4px;
  padding: 0px;
  font-weight: 400;
  font-size: 0.8rem;
}

.matpreview-dropdown-item {
  display: block;
  padding: 0.25rem 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.8rem;
  color: white;
  background-color: #252525;
  font-weight: bold;
  cursor: pointer;
}

.matpreview-dropdown-item:hover {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}

.matpreview-dropdown-item:hover {
  color: #16181b;
  text-decoration: none;
}

.matpreview-dropdown-item:hover {
  background-color: #f0f0f0;
}

// @media (max-width: 1610px) {
//   .mat-table-material {
//     width: 55%;
//   }
//   .mat-table-color {
//     width: 15%;
//   }
//   .mat-table-tiling {
//     width: 25%;
//   }
//   .mat-table-delete {
//     width: 5%;
//   }
// }

@media (max-width: 1350px) {
  .matpreview {
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    position: relative;
    border-radius: 5px;
    background-color: #28272d;
    box-sizing: border-box;
  }

    .matpreview-container {
    position: relative;
    width: 100%;
    margin-left: 0;
    margin-top: 1rem;
  }
}

// Medium devices (tablets, less than 992px)
@media (max-width: 991.98px) {
  .matpreview {
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    position: relative;
    border-radius: 5px;
    background-color: #28272d;
    box-sizing: border-box;
  }

  .matpreview-container {
    position: relative;
    width: 100%;
    margin-left: 0;
    margin-top: 1rem;
  }
}
</style>
